import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Award, BookOpen, ChevronRight, GraduationCap, Languages, Map as MapIcon, Mic, Trophy, Video,
} from 'lucide-react';
import { listenToOpenSessions, type OpenSession } from '@core/firebase/sessions';
import { useLastPosition } from '@core/firebase/progress';
import { isAttended } from '@core/session/presence';
import { endonym, isLanguage, teachesIn, type LanguageCode } from '@core/i18n/languages';
import { surahMeta } from '@core/quran/surahMeta';
import { useMyCurricula, useSchooling } from '@core/firebase/curricula';
import { useMyProfile } from '@core/firebase/profile';
import { useCoverage } from '@core/firebase/tracks';
import { useBadgeRules, useWallet, type BadgeRule, type Wallet } from '@core/firebase/gamification';
import { useCertificates } from '@core/firebase/certificates';
import {
  boundsOf, curriculumProgress, firstUncoveredAyah, nextUnit, unitAyahs, unitProgress,
} from '@core/quran/curriculumProgress';
import { curriculumMap, mapTally } from '@core/gamification/curriculumMap';
// Already in the bundle: curriculumProgress reads ayah counts from the same source.
import { bundledQuran } from '@core/quran/source';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { GenderPrompt } from '@/components/GenderPrompt';
import { StarLattice } from '@/components/StarLattice';
import { useManagedBy } from '@core/firebase/children';
import { cn } from '@/lib/cn';

/** The next badge still to win, and how far along the way to it the child is. */
function nextBadge(rules: BadgeRule[], wallet: Wallet): { rule: BadgeRule; have: number } | null {
  const value = (metric: BadgeRule['metric']) => (metric === 'points' ? wallet.earned : wallet[metric]);
  const open = rules
    .filter((r) => r.enabled && r.threshold > 0 && !(r.id in wallet.badges))
    .map((rule) => ({ rule, have: value(rule.metric) }))
    // Closest first, by share of the way there: the one the child is nearest.
    .sort((a, b) => (b.have / b.rule.threshold) - (a.have / a.rule.threshold));
  return open[0] ?? null;
}

/**
 * The first screen a signed-in طالب sees — redesign 2026-10 (DESIGN.md, the
 * approved web mock): a greeting, then ONE thing, the hero «وِردك اليوم»
 * holding the next āya of the pupil's own curriculum and the one main action;
 * beside it how far through the term they are; under it «تابع رحلتك» — map,
 * badges, certificates, each with the same footer; and the live teachers as a
 * slim row.
 *
 * No «آخر تمرين» card: practices keep only the LAST attempt, on the practice
 * page itself, and this page has never read it. The last position a معلم
 * recorded is what it has, and that is what the second card shows.
 *
 * Two session filters, both carried over from the mobile app and both
 * load-bearing: a session whose معلم has not beaten in 45s is not listed,
 * because joining it means waiting in an empty room; and a معلم is never
 * offered their own session, because they cannot be both sides of it.
 */
export default function StudentHomePage() {
  const { identity } = useAuth();
  const { t, language } = useLanguage();
  const uid = identity?.userId ?? null;
  const [sessions, setSessions] = useState<OpenSession[]>([]);
  const lastPosition = useLastPosition(uid);

  useEffect(() => listenToOpenSessions(setSessions), []);

  /*
   * م-7-02: one filter, and it filters by the language the pupil is already
   * reading the app in. Off by default — hiding teachers before anybody asked
   * is how a pupil concludes nobody is available.
   */
  const appLanguage: LanguageCode = isLanguage(language) ? language : 'ar';
  const [myLanguageOnly, setMyLanguageOnly] = useState(false);

  /*
   * Sessions are same-gender, and the callable refuses any other pair — so the
   * list shows only what a pupil could actually be admitted to. A pupil with
   * no gender recorded sees nobody and is told why: quietly listing everyone
   * would be applying the rule to nobody.
   */
  const profile = useMyProfile(uid);
  const myGender = profile.gender;
  const { managedBy, loading: managedLoading } = useManagedBy(uid);

  const live = sessions.filter(
    (s) => s.muqriId !== identity?.userId
      && isAttended(s, Date.now())
      && myGender !== null
      && s.muqriGender === myGender
  );
  const joinable = myLanguageOnly
    ? live.filter((s) => teachesIn(s.languages, appLanguage))
    : live;

  // The light table, not the full text: this page needs a name.
  const surahName = (n: number) => {
    const meta = surahMeta(n);
    if (!meta) return String(n);
    return language === 'ar' ? meta.name : meta.englishName;
  };
  // The Arabic name already reads «سُورَةُ …»; only the English one needs the word.
  const surahTitle = (n: number) => (language === 'ar' ? surahName(n) : t('learn.home.surah', { name: surahName(n) }));

  // Western digits in both languages — the app never uses Arabic-Indic ones.
  const number = (n: number) => new Intl.NumberFormat('en-US').format(n);
  const pct = (done: number, total: number) => (total > 0 ? Math.round((done / total) * 100) : 0);

  /*
   * م-2-04: the home is built around the pupil's own curriculum. The next
   * thing to recite comes from nextUnit, which walks the units in the order
   * the school set them — "بترتيب المنهج لا بترتيب المصحف".
   */
  const schooling = useSchooling(uid);
  const { curricula } = useMyCurricula(schooling.country, schooling.grade);
  const coverage = useCoverage(uid);
  const term = curricula[0] ?? null;
  const progress = term ? curriculumProgress(coverage, term.units) : null;
  const next = term ? nextUnit(coverage, term.units) : null;
  const tally = term ? mapTally(curriculumMap({ coverage, units: term.units })) : null;
  const noGrade = !schooling.loading && !term && !schooling.grade;

  const heroAyah = next ? firstUncoveredAyah(coverage, next) : 1;
  const ayahText = next ? bundledQuran.ayah(next.surah, heroAyah) : null;

  const wallet = useWallet(uid);
  const { rules } = useBadgeRules();
  const badge = wallet.loading ? null : nextBadge(rules, wallet);
  const badgeCount = Object.keys(wallet.badges).length;

  const { certificates } = useCertificates(uid);
  const certifiedSurahs = new Set(certificates.map((c) => c.surah));
  const certifiedUnits = term ? term.units.filter((u) => certifiedSurahs.has(u.surah)).length : 0;

  const hero = (() => {
    if (!term) return null;
    if (!next) {
      return (
        <section data-testid="home-hero" className="hero-card flex min-h-[240px] flex-col justify-center gap-3 p-7 lg:p-8">
          <StarLattice />
          <span className="relative w-fit rounded-full bg-lime/20 px-3.5 py-1 text-sm text-lime">{t('learn.home.wird')}</span>
          <p data-testid="term-done" className="relative text-2xl font-semibold">{t('student.termDone')}</p>
        </section>
      );
    }
    const start = boundsOf(next)?.[0] ?? 1;
    const unitPercent = unitProgress(coverage, next).percent;
    return (
      <section data-testid="home-hero" className="hero-card flex min-h-[330px] flex-col gap-3.5 p-7 lg:p-8">
        <StarLattice />
        <div className="relative flex items-center justify-between gap-3">
          <span className="rounded-full bg-lime/20 px-3.5 py-1 text-sm text-lime">{t('learn.home.wird')}</span>
          <span data-testid="hero-ayah-of" className="text-[15px] opacity-80">
            {t('learn.home.ayahOf', { n: number(heroAyah - start + 1), total: number(unitAyahs(next)) })}
          </span>
        </div>
        <h2 data-testid="next-unit" className="relative truncate text-[28px] font-semibold lg:text-[30px]">
          {surahTitle(next.surah)}
        </h2>
        <p
          data-testid="hero-ayah"
          lang="ar"
          className="ayah relative min-h-[4.5rem] font-quran text-[32px] lg:text-[40px]"
        >
          {ayahText ?? ''}
        </p>
        <div
          role="progressbar"
          aria-label={surahName(next.surah)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={unitPercent}
          className="relative mt-auto h-1.5 overflow-hidden rounded-full"
        >
          {/* The wash takes the hero's own text colour, so it reads in both themes. */}
          <div className="absolute inset-0 bg-current opacity-20" />
          <div className="relative h-full rounded-full bg-lime" style={{ width: `${unitPercent}%` }} />
        </div>
        <div className="relative flex flex-wrap items-center gap-x-4 gap-y-2">
          <Link
            to="/practice"
            data-testid="start-reciting"
            className="inline-flex h-14 items-center gap-2 rounded-full bg-lime px-8 text-[17px] font-semibold text-accent-foreground shadow-[0_8px_24px_rgba(198,198,50,0.25)] transition-transform hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
          >
            <Mic className="h-5 w-5" strokeWidth={1.9} aria-hidden="true" />
            {t('learn.home.start')}
          </Link>
          <span className="text-[15px] opacity-80">{t('learn.practice.capNote')}</span>
        </div>
      </section>
    );
  })();

  const ring = term && progress && tally ? (
    <Link
      to="/curriculum"
      data-testid="home-curriculum"
      className="card-soft flex items-center gap-5 p-6 transition-shadow hover:shadow-lg"
    >
      <Ring percent={progress.percent} label={number(progress.percent)} />
      <span className="min-w-0">
        <span className="block text-lg font-semibold text-ink">{t('learn.home.termProgress')}</span>
        <span className="block text-[15px] text-helper">
          {term.name}
          {' · '}
          {t('curriculum.unitsDone', { count: tally.total, done: number(tally.done) })}
        </span>
        <span data-testid="open-curriculum" className="mt-1 block text-sm font-medium text-primary">
          {t('student.openCurriculum')}
        </span>
      </span>
    </Link>
  ) : null;

  // The last position a معلم recorded — what this page has always read.
  const lastCard = lastPosition ? (
    <div className="card-soft flex items-center gap-4 p-6">
      <IconTile><BookOpen className="h-[22px] w-[22px]" strokeWidth={1.8} aria-hidden="true" /></IconTile>
      <p data-testid="last-position" className="min-w-0 text-[15px] font-medium text-ink">
        {t('muqri.lastReached', {
          surah: surahName(lastPosition.surah),
          ayah: number(lastPosition.ayah),
        })}
      </p>
    </div>
  ) : null;

  const mapFooter = next
    ? `${t('learn.track.next')}: ${surahTitle(next.surah)}`
    : term ? t('student.termDone')
      : schooling.grade ? t('game.map.none') : t('curriculum.noSchool');

  return (
    <div className="mx-auto w-full max-w-[1060px] space-y-6">
      {/* Two lines, not one interpolated string: a long name gets the whole
          width and its own ellipsis instead of clipping the greeting. */}
      <header>
        <p className="text-base text-helper">{t('student.greeting')}</p>
        <h1 data-testid="home-name" className="truncate text-[30px] font-semibold tracking-tight text-ink lg:text-[32px]">
          {identity?.displayName ?? ''}
        </h1>
      </header>

      {/* No grade → no syllabus to show. The banner IS the way to fix it. */}
      {noGrade && (
        <div data-testid="no-school-yet" className="card-soft flex items-center gap-4 p-4 lg:p-5">
          <LimeDisc><GraduationCap className="h-5 w-5" strokeWidth={1.8} aria-hidden="true" /></LimeDisc>
          <p className="min-w-0 flex-1 text-[15px] text-ink">{t('student.noSchoolYet')}</p>
          <Link
            to="/profile"
            data-testid="set-grade"
            className="inline-flex h-11 shrink-0 items-center rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground"
          >
            {t('learn.home.gradeAction')}
          </Link>
        </div>
      )}

      {(hero || ring || lastCard) && (
        <div className={cn('grid gap-6', hero && (ring || lastCard) && 'lg:grid-cols-[minmax(0,1.75fr)_minmax(0,1fr)]')}>
          {hero}
          {(ring || lastCard) && (
            <div className="flex flex-col gap-6 [&>*]:flex-1">
              {ring}
              {lastCard}
            </div>
          )}
        </div>
      )}

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-ink">{t('learn.home.journey')}</h2>
        <div className="grid gap-6 md:grid-cols-3">
          <JourneyTile
            testId="tile-map"
            to="/map"
            icon={<MapIcon className="h-[22px] w-[22px]" strokeWidth={1.8} aria-hidden="true" />}
            title={t('learn.home.map')}
            sub={tally ? t('learn.home.mapSub', { done: number(tally.done), total: number(tally.total) }) : t('game.map.title')}
            percent={tally?.percent ?? 0}
            footer={mapFooter}
          />
          <JourneyTile
            testId="tile-rewards"
            to="/rewards"
            icon={<Trophy className="h-[22px] w-[22px]" strokeWidth={1.8} aria-hidden="true" />}
            title={t('learn.home.rewards')}
            sub={`${t('game.store.cost', { n: number(wallet.balance) })} · ${t('learn.home.rewardsSub', { n: number(badgeCount) })}`}
            percent={badge ? pct(badge.have, badge.rule.threshold) : badgeCount > 0 ? 100 : 0}
            footer={badge
              ? `${t('game.badges.remaining', { n: number(Math.max(0, badge.rule.threshold - badge.have)) })} ${t(`game.metric.${badge.rule.metric}`)} · ${badge.rule.name}`
              : badgeCount > 0 ? t('game.badges.earned') : t('game.badges.noneBody')}
          />
          <JourneyTile
            testId="tile-certificates"
            to="/certificates"
            icon={<Award className="h-[22px] w-[22px]" strokeWidth={1.8} aria-hidden="true" />}
            title={t('certificates.title')}
            sub={certificates.length > 0 ? surahName(certificates[0].surah) : t('certificates.none')}
            percent={term ? pct(certifiedUnits, term.units.length) : 0}
            footer={term
              ? t('curriculum.unitsDone', { count: term.units.length, done: number(certifiedUnits) })
              : t('certificates.subtitle')}
          />
        </div>
      </section>

      <section className="space-y-3">
        {/* Signup no longer asks (App Review 5.1.1(v)); this is where it is
            needed, so this is where it is asked. */}
        {myGender === null && !profile.loading && !managedLoading && (
          // A managed child may not edit their own profile; their guardian
          // answers for them, from the children list.
          managedBy
            ? (
              <p data-testid="gender-needed" className="rounded-[20px] bg-tint p-4 text-sm text-ink">
                {t('student.genderAskParent')}
              </p>
            )
            : <GenderPrompt message={t('student.genderNeeded')} />
        )}

        {live.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-xl font-semibold text-ink">{t('student.availableNow')}</h2>
            <button
              type="button"
              data-testid="my-language-only"
              role="switch"
              aria-checked={myLanguageOnly}
              onClick={() => setMyLanguageOnly((on) => !on)}
              className={cn(
                'inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm transition-colors',
                myLanguageOnly
                  ? 'bg-primary font-semibold text-primary-foreground'
                  : 'bg-card text-ink shadow-sm hover:bg-surface',
              )}
            >
              <Languages className="h-4 w-4" strokeWidth={1.8} aria-hidden="true" />
              {t('student.myLanguageOnly', { language: endonym(appLanguage) })}
            </button>
          </div>
        )}

        {joinable.length === 0 ? (
          // One slim row: who can hear a recitation right now — nobody, yet.
          <div data-testid="no-sessions" className="card-soft flex items-center gap-4 rounded-[20px] px-5 py-3.5">
            <IconTile small><Video className="h-5 w-5" strokeWidth={1.8} aria-hidden="true" /></IconTile>
            <p className="min-w-0 flex-1 text-sm text-helper">
              <span className="me-2.5 text-base font-semibold text-ink">
                {myLanguageOnly && live.length > 0
                  ? t('student.noneInLanguage', { language: endonym(appLanguage) })
                  : t('student.noneAvailableTitle')}
              </span>
              {myLanguageOnly && live.length > 0
                ? t('student.noneInLanguageBody')
                : t('student.noneAvailableBody')}
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {joinable.map((s) => (
              <li
                key={s.id}
                data-testid={`session-${s.id}`}
                className="card-soft flex items-center gap-4 rounded-[20px] px-5 py-3.5"
              >
                <IconTile small><Video className="h-5 w-5" strokeWidth={1.8} aria-hidden="true" /></IconTile>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-ink">{s.muqriName}</p>
                  <p className="truncate text-sm text-helper">
                    {t(s.mode === 'halaqa' ? 'muqri.modeHalaqa' : 'muqri.modePrivate')}
                    {' · '}
                    {t('student.waitingCount', { count: number(s.waitingCount) })}
                    {s.languages.length > 0 ? ` · ${s.languages.map(endonym).join(' · ')}` : ''}
                  </p>
                </div>
                <Link
                  to={`/waiting/${s.id}`}
                  data-testid={`join-${s.id}`}
                  className="inline-flex h-11 shrink-0 items-center rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground"
                >
                  {t('student.join')}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** The progress ring: lime on the hairline track, the share in the middle. */
function Ring({ percent, label }: { percent: number; label: string }) {
  const r = 29;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <svg
      width="72"
      height="72"
      viewBox="0 0 72 72"
      role="img"
      aria-label={`${label}%`}
      className="shrink-0"
    >
      <circle cx="36" cy="36" r={r} fill="none" strokeWidth="8" className="stroke-hair" />
      <circle
        cx="36"
        cy="36"
        r={r}
        fill="none"
        strokeWidth="8"
        strokeLinecap="round"
        strokeDasharray={`${(clamped / 100) * c} ${c}`}
        transform="rotate(-90 36 36)"
        className="stroke-lime"
      />
      <text x="36" y="41" textAnchor="middle" fontSize="14" fontWeight="600" className="fill-ink">
        {label}%
      </text>
    </svg>
  );
}

function IconTile({ children, small = false }: { children: ReactNode; small?: boolean }) {
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center bg-tint text-primary',
        small ? 'h-[42px] w-[42px] rounded-[14px]' : 'h-12 w-12 rounded-2xl',
      )}
    >
      {children}
    </span>
  );
}

/** The empty-state mark (DESIGN.md): a lime disc. */
function LimeDisc({ children }: { children: ReactNode }) {
  return (
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-lime text-accent-foreground">
      {children}
    </span>
  );
}

/** «تابع رحلتك»: every tile the same shape — a bar, then one footer line. */
function JourneyTile({
  testId, to, icon, title, sub, percent, footer,
}: {
  testId: string; to: string; icon: ReactNode; title: string; sub: string; percent: number; footer: string;
}) {
  const clamped = Math.max(0, Math.min(100, Math.round(percent)));
  return (
    <Link
      to={to}
      data-testid={testId}
      className="card-soft group flex flex-col p-5 transition-shadow hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <span className="flex items-center gap-3.5">
        <IconTile>{icon}</IconTile>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[17px] font-semibold text-ink">{title}</span>
          <span className="block truncate text-sm text-helper">{sub}</span>
        </span>
        <ChevronRight
          className="h-[18px] w-[18px] shrink-0 text-helper transition-transform group-hover:translate-x-0.5 rtl:-scale-x-100 rtl:group-hover:-translate-x-0.5"
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </span>
      <span
        role="progressbar"
        aria-label={title}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={clamped}
        className="mt-[18px] block h-1.5 overflow-hidden rounded-full bg-hair"
      >
        <span className="block h-full rounded-full bg-lime" style={{ width: `${clamped}%` }} />
      </span>
      <span className="mt-2 block truncate text-sm text-helper">{footer}</span>
    </Link>
  );
}
